import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faAngleDown,
  faAngleUp,
  faLock,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import {
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { sortByArrayIndex } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { HexColor } from '@sorare/core/src/style/types';

import { EligibleCards } from '../EligibleCards';
import type { LeagueSelect_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 320px;
  padding: var(--intermediate-unit) var(--double-unit);
  transition: 0.2s ease-in-out;
  .composable &:hover {
    background-color: var(--c-nd-50);
  }
`;
const OptionContainer = styled.div`
  position: relative;
  &:not(.composable) {
    opacity: 0.5;
    &:hover {
      opacity: 0.65;
    }
  }
`;

type Props = {
  selectedSo5LeaderboardGroupSlug: string;
  so5LeaderboardGroupInterfaces: LeagueSelect_so5LeaderboardGroupInterface[];
  rarity: Rarity | null;
  onClick: (args: {
    competition: string;
    trckGrp: string;
    trck: string;
  }) => void;
  allowNavigatingWhenLocked?: boolean;
};

export const LeagueSelect = ({
  selectedSo5LeaderboardGroupSlug,
  so5LeaderboardGroupInterfaces,
  rarity,
  onClick,
  allowNavigatingWhenLocked = false,
}: Props) => {
  const { sport } = useSportContext();

  const selectedSo5LeaderboardGroupInterface =
    so5LeaderboardGroupInterfaces.find(
      ({ slug }) => selectedSo5LeaderboardGroupSlug === slug
    ) || so5LeaderboardGroupInterfaces[0];

  if (!selectedSo5LeaderboardGroupInterface) return null;

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <Button
          disableDebounce
          color="tertiary"
          size="small"
          startIcon={
            <Dot
              size={1}
              color={selectedSo5LeaderboardGroupInterface.color as HexColor}
            />
          }
          endIcon={<FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
          className={classNames({ active: open })}
          {...btnProps}
        >
          <LabelL>{selectedSo5LeaderboardGroupInterface.displayName}</LabelL>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <>
          {so5LeaderboardGroupInterfaces.map(so5LeaderboardGroupInterface => {
            const trckGroupWithSameRarity =
              so5LeaderboardGroupInterface.so5LeagueTrackGroups?.find(
                ({ mainRarityType }) => mainRarityType === rarity
              );

            const isComposable = trckGroupWithSameRarity?.so5LeagueTracks.some(
              trck => trck.canCompose.value
            );

            const anyComposableTrckGrp =
              so5LeaderboardGroupInterface.so5LeagueTrackGroups.find(
                ({ so5LeagueTracks }) =>
                  so5LeagueTracks.some(trck => trck.canCompose.value)
              );

            const trckGroup = isComposable
              ? trckGroupWithSameRarity
              : anyComposableTrckGrp;

            const composableTrcks =
              trckGroup?.so5LeagueTracks.filter(
                ({ canCompose }) => canCompose.value
              ) || [];
            const trck =
              composableTrcks.find(
                ({ seasonality }) =>
                  seasonality === So5LeaderboardSeasonality.IN_SEASON
              ) || composableTrcks[0];

            const onClickCb = () => {
              onClick({
                competition: so5LeaderboardGroupInterface.slug,
                trckGrp: trckGroup?.slug || '',
                trck: trck?.slug || '',
              });
              closeDropdown();
            };

            const contenders =
              trckGroup?.so5LeagueTracks.flatMap(
                ({ mySo5LeaderboardContenders }) => mySo5LeaderboardContenders
              ) || [];

            const contendersWithALineup = contenders.filter(
              ({ so5Lineup }) => so5Lineup?.confirmable && !so5Lineup.draft
            );

            const isSpecialWeekly =
              isType(so5LeaderboardGroupInterface, 'So5League') &&
              so5LeaderboardGroupInterface.name === 'weekly';

            const showChip = contenders.length > 0 || isSpecialWeekly;

            let chipColor: ComponentProps<typeof Chip>['color'] =
              contendersWithALineup.length === contenders.length
                ? 'green'
                : 'transparent';
            if (isSpecialWeekly && contenders.length === 0) {
              chipColor = 'blue';
            }

            const isClickable =
              so5LeaderboardGroupInterface.canCompose.value ||
              allowNavigatingWhenLocked;

            return (
              <OptionContainer
                key={so5LeaderboardGroupInterface.slug}
                className={classNames({
                  composable: isClickable,
                })}
              >
                <Tooltip
                  title={
                    sport === Sport.FOOTBALL && (
                      <Vertical>
                        <LabelL color="var(--c-nd-600)">
                          <FormattedMessage {...fantasy.eligibleCards} />
                        </LabelL>
                        {[...so5LeaderboardGroupInterface.seasonalities]
                          .sort((a, b) =>
                            sortByArrayIndex(
                              [
                                So5LeaderboardSeasonality.IN_SEASON,
                                So5LeaderboardSeasonality.ALL_SEASONS,
                              ],
                              a,
                              b
                            )
                          )
                          .map(s => (
                            <Horizontal key={s}>
                              <SportSeasonalityIcon size={2} seasonality={s} />
                              <EligibleCards
                                seasonality={s}
                                so5LeaderboardGroupInterface={
                                  so5LeaderboardGroupInterface
                                }
                              />
                            </Horizontal>
                          ))}
                      </Vertical>
                    )
                  }
                  placement="right"
                >
                  <Option
                    {...(isClickable
                      ? {
                          as: ButtonBase,
                          onClick: onClickCb,
                        }
                      : {})}
                  >
                    <Horizontal>
                      <Dot
                        size={1}
                        color={so5LeaderboardGroupInterface.color as HexColor}
                      />
                      <LabelL
                        color={
                          selectedSo5LeaderboardGroupSlug ===
                          so5LeaderboardGroupInterface.slug
                            ? 'var(--c-white)'
                            : 'var(--c-nd-600)'
                        }
                      >
                        {so5LeaderboardGroupInterface.displayName}
                      </LabelL>
                    </Horizontal>

                    {isClickable ? (
                      <>
                        {!!showChip && (
                          <Chip color={chipColor}>
                            {isSpecialWeekly && contenders.length === 0 ? (
                              <FormattedMessage {...glossary.new} />
                            ) : (
                              <>
                                {contendersWithALineup.length}
                                {' / '}
                                {contenders.length}
                              </>
                            )}
                          </Chip>
                        )}
                      </>
                    ) : (
                      <FontAwesomeIcon icon={faLock} />
                    )}
                  </Option>
                </Tooltip>
              </OptionContainer>
            );
          })}
        </>
      )}
    </Dropdown>
  );
};

LeagueSelect.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment LeagueSelect_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      ... on So5League {
        slug
        name
      }
      color(variant: PRIMARY)
      displayName
      iconUrl
      seasonalities
      canCompose {
        value
      }
      so5LeagueTrackGroups {
        slug
        seasonalities
        mainRarityType
        so5LeagueTracks {
          slug
          seasonality
          completed
          canCompose {
            value
          }
          mySo5LeaderboardContenders {
            slug
            so5Lineup {
              id
              draft
              confirmable
            }
          }
        }
      }
      ...EligibleCards_so5LeaderboardGroupInterface
    }
    ${EligibleCards.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<LeagueSelect_so5LeaderboardGroupInterface>,
};
